import { useMemo } from 'react';
import {
    FunctionField,
    RecordContextProvider,
    ReferenceField,
    SimpleShowLayout,
    TextField,
    useGetList,
    useGetMany,
    useRecordContext,
    useTranslate,
} from 'react-admin';
import { Box, Link, Typography, useMediaQuery, useTheme } from '@mui/material';
import { DetailsSampleList } from '@/components/global/fields/DetailsSampleList.jsx';
import { EmptyField } from '@/components/accessKeys/fields/EmptyField.jsx';
import CustomChipField from '@/components/global/fields/CustomChipField.jsx';
import { BrandLogoField } from '@/components/brand/fields/BrandLogoField.jsx';
import { useDrawer } from '@/composables/useDrawer';
import { Palette } from '@/themes/themeOverrides/Palette';

const RetailChainDetailsLink = () => {
    const retailChain = useRecordContext();
    const { openDrawer } = useDrawer();

    if (!retailChain?.id || !retailChain?.name) {
        return <EmptyField />;
    }

    return (
        <Typography variant='body2'>
            <Link
                component='button'
                type='button'
                underline='hover'
                color='inherit'
                onClick={event => {
                    event.stopPropagation();
                    openDrawer(retailChain.id, 'retail-chain-show');
                }}
                sx={{ cursor: 'pointer', border: 'none', background: 'none', padding: 0, font: 'inherit', verticalAlign: 'baseline' }}
            >
                {retailChain.name}
            </Link>
        </Typography>
    );
};

RetailChainDetailsLink.propTypes = {};

const contractorRecords = value => (Array.isArray(value) ? value.filter(item => item && typeof item === 'object' && item.id) : []);

export const BrandBasicDetails = () => {
    const translate = useTranslate();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

    return (
        <SimpleShowLayout className={isDesktop ? 'two-columns-show-layout' : ''}>
            <SimpleShowLayout className='no-label image' sx={{ marginBottom: isDesktop ? '0' : '32px', maxWidth: 'max-content' }}>
                <FunctionField source='logo' label={translate('resources.brand.fields.logo')} render={() => <BrandLogoField column />} />
            </SimpleShowLayout>
            <DetailsSampleList>
                <TextField source='name' label={translate('resources.brand.fields.name')} emptyText='—' />
                <FunctionField source='retailChainId' label={translate('resources.brand.fields.retailChain')} render={() => <BrandRetailChainDetails />} />
                <TextField source='description' label={translate('resources.brand.fields.description')} emptyText='—' />
            </DetailsSampleList>
        </SimpleShowLayout>
    );
};

BrandBasicDetails.propTypes = {};

const BrandRetailChainDetails = () => {
    const record = useRecordContext();

    if (!record?.retailChainId) {
        return <EmptyField />;
    }

    return (
        <ReferenceField source='retailChainId' reference='retail-chain' link={false}>
            <RetailChainDetailsLink />
        </ReferenceField>
    );
};

BrandRetailChainDetails.propTypes = {};

export const BrandContractorsDetails = () => {
    const record = useRecordContext();
    const { openDrawer } = useDrawer();
    const embeddedContractors = contractorRecords(record?.contractors);
    const contractorIds = useMemo(() => {
        const ids = record?.contractorIds;
        return Array.isArray(ids) ? ids.filter(Boolean) : [];
    }, [record?.contractorIds]);
    const hasEmbeddedContractors = Array.isArray(record?.contractors);
    const { data: contractorsById = [], isPending: isIdsPending } = useGetMany(
        'contractor',
        { ids: contractorIds },
        { enabled: !hasEmbeddedContractors && contractorIds.length > 0 },
    );
    const { data: contractorsByBrand = [], isPending: isListPending } = useGetList(
        'contractor',
        {
            pagination: { page: 1, perPage: 200 },
            sort: { field: 'name', order: 'ASC' },
            filter: { brandId: record?.id },
        },
        { enabled: Boolean(record?.id) && !hasEmbeddedContractors && contractorIds.length === 0 },
    );

    let contractors = contractorsByBrand;
    if (hasEmbeddedContractors) {
        contractors = embeddedContractors;
    } else if (contractorIds.length > 0) {
        contractors = contractorsById;
    }
    const isPending =
        (!hasEmbeddedContractors && contractorIds.length > 0 && isIdsPending) || (!hasEmbeddedContractors && contractorIds.length === 0 && isListPending);

    if (isPending) {
        return (
            <Typography variant='body2' component='span'>
                —
            </Typography>
        );
    }

    if (!contractors?.length) {
        return (
            <Typography variant='body2' component='span'>
                —
            </Typography>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {contractors.map(contractor => (
                <RecordContextProvider key={contractor.id} value={contractor}>
                    <CustomChipField shades color={Palette.gray.gray100} onClick={() => openDrawer(contractor.id, 'contractor-show')} />
                </RecordContextProvider>
            ))}
        </Box>
    );
};

BrandContractorsDetails.propTypes = {};
